/* SUMMARY
  * Imports
  * compInfoPhases: moments covered by a usage value
  * allowedCompInfoUsages: moments open for a workorder at a given status
  * isCompInfoUsable: whether the compInfo can be edited at that status
  * isCompInfoDisplayed: usable now, or already answered
  * stripUnusableCompInfos: drop the back's empty padding from a save payload
*/

/* Imports */
import { WorkorderStatus } from "../../../types/workorder/workorderStatus";
/***/

const PHASES_BY_USAGE: Record<string, string[]> = {
  CREATE: ["CREATE"],
  DURING: ["DURING"],
  AFTER: ["AFTER"],
  ALL: ["CREATE", "DURING", "AFTER"],
  CREATE_DURING: ["CREATE", "DURING"],
  CREATE_AFTER: ["CREATE", "AFTER"],
  DURING_AFTER: ["DURING", "AFTER"]
};

/**
 * Moments covered by a usage value. ALL is Create, During and After.
 *
 * @param compInfo - compInfo descriptor
 * @return - moments, empty when the value is missing
 */
export const compInfoPhases = (compInfo?: { usage?: string }): string[] =>
  PHASES_BY_USAGE[compInfo?.usage || ""] || [];
/***/

/**
 * Moments checked in the admin multi-select for a stored usage.
 */
export const usageMoments = (usage?: string): string[] => compInfoPhases({ usage });
/***/

/**
 * Stored usage for a multi-select. Each moment stays itself.
 * Create + During does not include After. The three moments together are ALL.
 */
export const usageFromMoments = (moments: string[]): string | undefined => {
  const selected = new Set(moments);
  const create = selected.has("CREATE");
  const during = selected.has("DURING");
  const after = selected.has("AFTER");
  if (create && during && after) return "ALL";
  if (create && during) return "CREATE_DURING";
  if (create && after) return "CREATE_AFTER";
  if (during && after) return "DURING_AFTER";
  if (create) return "CREATE";
  if (during) return "DURING";
  if (after) return "AFTER";
  return undefined;
};
/***/

/*
 * Create n'est ouvert que sur le formulaire de création, quand l'intervention n'a pas encore de
 * statut. During n'est ouvert qu'en IN_PROGRESS. After n'est ouvert qu'en CLOSED.
 * ALL couvre la création, l'intervention et la clôture.
 * CREATE_DURING couvre la création et l'intervention, pas la clôture.
 * CREATE_AFTER couvre la création et la clôture. DURING_AFTER couvre l'intervention et la clôture.
 */

/**
 * allowedCompInfoUsages: moments open for a workorder at a given status
 * @param status - current workorder status, absent while creating one
 * @return - moments whose compInfos can be edited and validated
 */
export const allowedCompInfoUsages = (status?: string): string[] => {
  if (!status) return ["CREATE"];
  if (status === WorkorderStatus.IN_PROGRESS) return ["DURING"];
  if (status === WorkorderStatus.CLOSED) return ["AFTER"];
  return [];
};
/***/

/**
 * isCompInfoUsable: whether this usage can be edited at the open moments
 * @param compInfo - compInfo descriptor
 * @param usages - moments returned by allowedCompInfoUsages
 * @return - usable or not
 */
export const isCompInfoUsable = (compInfo: EasyTech.CompInfo | undefined, usages: string[]): boolean => {
  if (!compInfo?.usage) return false;
  return compInfoPhases(compInfo).some((phase) => usages.includes(phase));
};
/***/

export const hasFilledCompInfoValue = (value: unknown): boolean =>
  value !== null && value !== undefined && String(value).trim() !== "";
/***/

/**
 * A field stays on the read-only screen when its moment is open or when it already has an answer,
 * so a value entered at creation does not disappear once the intervention exists.
 */
export const isCompInfoDisplayed = (
  compInfo: EasyTech.CompInfo | undefined,
  status: string | undefined,
  hasValue: boolean
): boolean => isCompInfoUsable(compInfo, allowedCompInfoUsages(status)) || hasValue;
/***/

/**
 * stripUnusableCompInfos: drop the back's empty padding from a save payload
 *
 * Le back complète l'intervention de tous les compInfos du service, vides compris
 * (consolidateCompInfos), et ce remplissage revient ici dans l'objet édité. On l'écarte pour ne pas
 * enregistrer une saisie que le formulaire n'a jamais proposée.
 *
 * Une entrée qui porte une valeur reste envoyée même si son moment de saisie est fermé : côté core,
 * mapInput fait un deleteAllByWoId avant de réécrire ce qu'on lui donne, donc l'omettre la
 * supprimerait.
 *
 * @param compInfos - compinfos about to be sent to the report endpoint
 * @param status - current workorder status
 * @return - compinfos worth sending
 */
export const stripUnusableCompInfos = (compInfos: EasyTech.CompInfos, status?: string): EasyTech.CompInfos => {
  const usages = allowedCompInfoUsages(status);

  return {
    ...compInfos,
    inputs: (compInfos.inputs || []).filter((el) => isCompInfoUsable(el.compInfo, usages) || hasFilledCompInfoValue(el.value)),
    selects: (compInfos.selects || []).filter((el) => isCompInfoUsable(el.compInfo, usages) || (el.values || []).length > 0)
  };
};
/***/
